
import React, { useContext, useState } from 'react';
import Modal from './Modal';
import Button from '../ui/Button';
import FileUpload from '../ui/FileUpload';
import RichTextEditor from '../ui/RichTextEditor';
import { getAxiosTokenInstance } from '../../../utils/axiosInstance';
import { message } from 'antd';
// import { string } from 'yup';
import { NecttosContext } from '../../../context/NecttosContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface GlossaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: GlossaryData) => void;
  subjectId: string;
  modules: any[]
}

interface GlossaryData {
  term: string;
  definition: string;
  attachments: {name: string, url: string};
  moduleId: string;
  subjectId: string
}

const GlossaryModal: React.FC<GlossaryModalProps> = ({ isOpen, onClose, onSubmit, subjectId, modules }) => {
  const {collegeId, teacherId} = useContext(NecttosContext);
  const [formData, setFormData] = useState<GlossaryData>({
    term: '',
    definition: '',
    attachments: {name: "", url: ""},
    moduleId: "",
    subjectId: subjectId
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.term.trim()) {
      newErrors.term = 'Term is required';
    }

    if (!formData.definition.trim()) {
      newErrors.definition = 'Definition is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleFileUpload = async (file: File) => {
    // const file = event.target.files ? event.target.files[0] : null;
  
    if (!file) {
      message.error('No file selected.');
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) {
      message.error(`File size must be less than 10 MB.`);
      return;
    }
  
    setIsUploading(true);
  
    try {
      const downloadURL = await uploadFileToFirebase(file);
      if (downloadURL) {
       setFormData(prev => ({
    ...prev,
    attachments: { name: file.name, url: downloadURL }
  }));
  
        message.success(`${file.name} uploaded successfully.`);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      message.error('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };
  
  
    const uploadFileToFirebase = async (file: File): Promise<string | null> => {
    const storage = getStorage();
    const filePath = `glossary/${subjectId}/${formData.moduleId}/${file.name}`;
    const fileRef = ref(storage, filePath);
  
    const snapshot = await uploadBytes(fileRef, file, {
      contentType: file.type
    });
  
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  };
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const instance = await getAxiosTokenInstance();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const resp = await instance.post(`/college/lms/glossary/createGlossary`, {...formData, collegeId: collegeId});
           if(resp?.data) {
              console.log(resp.data.message)
             message.success('glossary created')
            
           }
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error creating glossary entry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      term: '',
      definition: '',
      attachments: {name: "", url: ""},
      moduleId: '',
      subjectId: subjectId
    });
    setErrors({});
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Glossary Entry" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Term */}
        <div>
          <label htmlFor="term" className="block text-sm font-medium text-gray-700 mb-2">
            📖 Term/Concept *
          </label>
          <input
            type="text"
            id="term"
            value={formData.term}
            onChange={(e) => setFormData(prev => ({ ...prev, term: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.term ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter term or concept name"
          />
          {errors.term && <p className="mt-1 text-sm text-red-600">{errors.term}</p>}
        </div>
            
            <div>
  <label htmlFor="module" className="block text-sm font-medium text-gray-700 mb-2">
    Module *
  </label>

  <select
    id="module"
    value={formData.moduleId}
    onChange={(e) => setFormData(prev => ({ ...prev, moduleId: e.target.value }))}
    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      errors.module ? 'border-red-500' : 'border-gray-300'
    }`}
  >
    <option value="">Select a module</option>
    {modules?.map((mod) => (
      <option key={mod._id} value={mod._id}>
        {mod.fullModuleName}
      </option>
    ))}
  </select>

  {errors.module && <p className="mt-1 text-sm text-red-600">{errors.module}</p>}
</div>
        {/* Definition */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Definition & Description *
          </label>
          <RichTextEditor
            value={formData.definition}
            onChange={(value) => setFormData(prev => ({ ...prev, definition: value }))}
            placeholder="Enter detailed definition and description..."
            minHeight="200px"
          />
          {errors.definition && <p className="mt-1 text-sm text-red-600">{errors.definition}</p>}
        </div>

        {/* Supporting Files */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supporting Files (Optional)
          </label>
          <FileUpload
  onFilesSelected={async (files: File[]) => {
    const file = files[0];
   
   handleFileUpload(file);
  }}
  label="Upload assignment materials"
/>

          <p className="mt-1 text-xs text-gray-500">
            Accepted formats: PDF, Images, Word documents
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Adding...
              </span>
            ) : (
              'Add to Glossary'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default GlossaryModal;
